import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate, useNavigationType, type NavigateFunction } from 'react-router-dom';
import { isMacLike } from '../events/shortcuts';
import { navStore } from './navStore';
import { backKeyOf, canGoBack, canGoForward, recentPlaces } from './places';
import { useEscapeLayer } from '../ui/Dialog';

const useNavState = () => useSyncExternalStore(navStore.subscribe, navStore.snapshot);

/** The browser history entry's number (react-router keeps it in `history.state.idx`); 0 when unknown. */
const currentIdx = (): number => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : 0;
};

/**
 * Takes `delta` steps in the tab's history. The focused field is left first: a sheet row (quote, packlist) saves
 * when it is left, and going to another screen would otherwise drop what was typed in it.
 */
export function goSteps(navigate: NavigateFunction, delta: number) {
  if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) document.activeElement.blur();
  navigate(delta);
}

/** Records every screen the tab visits (in the app frame). */
export function useNavRecorder() {
  const location = useLocation();
  const type = useNavigationType();
  const first = useRef(true);
  const path = location.pathname + location.search;
  useEffect(() => {
    navStore.record({ idx: currentIdx(), path, action: first.current ? 'LOAD' : type });
    first.current = false;
    // a new entry or a replaced one always has a new key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, path]);
}

/** A screen's name in the list of recent places, e.g. "Kosztorys — Wydarzenie X" (null: the section's name). */
export function usePlaceTitle(title: string | null | undefined) {
  const location = useLocation();
  const path = location.pathname + location.search;
  useEffect(() => {
    if (title) navStore.setTitle(path, title);
  }, [path, title]);
}

const isTextField = (el: EventTarget | null) =>
  el instanceof HTMLTextAreaElement ||
  el instanceof HTMLSelectElement ||
  (el instanceof HTMLElement && el.isContentEditable) ||
  (el instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(el.type));

/** ⌘[ / ⌘] (Ctrl on other systems) and Alt+← / Alt+→: a step back or forward inside the app. */
export function useNavShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // not while an input method composes text, nor under a modal dialog (it owns the keyboard)
      if (e.defaultPrevented || e.isComposing || e.keyCode === 229 || document.querySelector('[aria-modal="true"]')) return;
      const delta = backKeyOf(e, isMacLike(), isTextField(e.target));
      if (delta === null) return;
      // the browser's own step would leave the app at its edge: the shortcut is always ours
      e.preventDefault();
      const { stack, idx } = navStore.snapshot();
      if (delta < 0 ? canGoBack(stack, idx) : canGoForward(stack, idx)) goSteps(navigate, delta);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);
}

const HOLD_MS = 500;

function Arrow({ dir }: { dir: -1 | 1 }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d={dir < 0 ? 'M10 3.5 5.5 8l4.5 4.5' : 'M6 3.5 10.5 8 6 12.5'} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "Wstecz" and "Dalej" (P13): a click takes a step; holding the button (or a right click, or ↓) lists the recent
 * places by name. Both are off when the step would leave the app.
 */
export function NavSteps({ className }: { className?: string }) {
  const { stack, idx } = useNavState();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<-1 | 1 | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const held = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const mac = isMacLike();
  const places = recentPlaces(stack, idx);

  useEffect(() => {
    if (menu === null) return;
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setMenu(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [menu]);
  useEscapeLayer(() => {
    root.current?.querySelector<HTMLElement>(`[data-dir="${menu}"]`)?.focus();
    setMenu(null);
  }, menu !== null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // a step elsewhere (shortcut, the browser's own button) closes the list
  useEffect(() => setMenu(null), [idx]);

  const button = (dir: -1 | 1) => {
    const enabled = dir < 0 ? canGoBack(stack, idx) : canGoForward(stack, idx);
    const label = dir < 0 ? 'Wstecz' : 'Dalej';
    const keys = dir < 0 ? (mac ? '⌘[' : 'Ctrl+[') : mac ? '⌘]' : 'Ctrl+]';
    const stop = () => window.clearTimeout(timer.current);
    return (
      <button
        type="button"
        className="btn ghost icon nav-step"
        data-dir={dir}
        aria-label={label}
        title={`${label} (${keys}) — przytrzymaj, żeby zobaczyć listę`}
        aria-keyshortcuts={dir < 0 ? 'Meta+[ Control+[ Alt+ArrowLeft' : 'Meta+] Control+] Alt+ArrowRight'}
        aria-haspopup="menu"
        aria-expanded={menu === dir}
        aria-controls={menu === dir ? menuId : undefined}
        disabled={!enabled}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          held.current = false;
          stop();
          timer.current = window.setTimeout(() => {
            held.current = true;
            setMenu(dir);
          }, HOLD_MS);
        }}
        onPointerUp={stop}
        onPointerLeave={() => {
          stop();
          held.current = false; // a hold that ended elsewhere does not swallow the next click
        }}
        onPointerCancel={() => {
          stop();
          held.current = false;
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          stop();
          setMenu(dir);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowDown') return;
          e.preventDefault();
          setMenu(dir);
        }}
        onClick={() => {
          if (held.current) {
            held.current = false; // the hold opened the list; letting go is not a step
            return;
          }
          setMenu(null);
          goSteps(navigate, dir);
        }}
      >
        <Arrow dir={dir} />
      </button>
    );
  };

  const list = menu === null ? [] : menu < 0 ? places.back : places.forward;
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') setMenu(null);
  };

  return (
    <div className={['nav-steps', className].filter(Boolean).join(' ')} ref={root} data-feature-id="hints.navigation">
      {button(-1)}
      {button(1)}
      {menu !== null && list.length > 0 && (
        <div className={`row-menu-pop nav-steps-pop${menu > 0 ? ' ahead' : ''}`} role="menu" id={menuId} aria-label={menu < 0 ? 'Wcześniejsze miejsca' : 'Dalsze miejsca'} onKeyDown={onMenuKey}>
          {list.map((p) => (
            <button
              key={p.idx}
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(null);
                goSteps(navigate, p.delta);
              }}
            >
              {p.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
